import { getApiBase, mediaApiUrl } from "./apiConfig";
import { adminAuthedFetch } from "./adminAuthedFetch";
import { getAdminJwt } from "./authToken";

const STORAGE_KEY = "vii_media_library_v1";

export class MediaApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "MediaApiError";
    this.status = status;
    this.body = body;
  }
}

function readJsonStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeJsonStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

function newId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `m_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}

function extractUrlFromUploadBody(body) {
  if (!body || typeof body !== "object") return "";
  const u =
    body.url ||
    body.secure_url ||
    body.fileUrl ||
    body.imageUrl ||
    body.coverUrl ||
    (body.data && (body.data.url || body.data.secure_url)) ||
    (body.result && (body.result.url || body.result.secure_url));
  return typeof u === "string" && u.trim() ? u.trim() : "";
}

function normalizeListItem(raw, i) {
  if (typeof raw === "string" && raw.trim()) {
    return { id: newId(), url: raw.trim(), name: "", createdAt: new Date().toISOString() };
  }
  if (!raw || typeof raw !== "object") return null;
  const url =
    raw.url ||
    raw.secure_url ||
    raw.fileUrl ||
    raw.imageUrl ||
    (typeof raw.src === "string" ? raw.src : "");
  if (!url || typeof url !== "string") return null;
  const id = raw.id != null ? String(raw.id) : raw._id != null ? String(raw._id) : newId();
  const name = typeof raw.name === "string" ? raw.name : typeof raw.filename === "string" ? raw.filename : "";
  const createdAt =
    typeof raw.createdAt === "string"
      ? raw.createdAt
      : typeof raw.uploadedAt === "string"
        ? raw.uploadedAt
        : new Date().toISOString();
  return { id, url: url.trim(), name, createdAt };
}

function normalizeListResponse(data) {
  if (!data) return [];
  const arr = Array.isArray(data) ? data : data.items || data.media || data.files || data.data || [];
  if (!Array.isArray(arr)) return [];
  const out = [];
  arr.forEach((raw, i) => {
    const row = normalizeListItem(raw, i);
    if (row) out.push(row);
  });
  return out;
}

export function readStoredMedia() {
  return readJsonStorage();
}

export function addStoredMediaItem({ url, name = "" }) {
  if (!url || typeof url !== "string") return null;
  const items = readJsonStorage();
  if (items.some((x) => x.url === url)) return items.find((x) => x.url === url);
  const row = { id: newId(), url: url.trim(), name: name || "", createdAt: new Date().toISOString() };
  const next = [row, ...items];
  writeJsonStorage(next);
  return row;
}

export function removeStoredMediaItem(id) {
  const items = readJsonStorage();
  const next = items.filter((x) => x.id !== id);
  writeJsonStorage(next);
}

export async function fetchMediaLibrary() {
  const base = getApiBase();
  const token = getAdminJwt();
  if (base && token) {
    const url = mediaApiUrl("list");
    if (url) {
      try {
        const res = await adminAuthedFetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const text = await res.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          data = {};
        }
        if (res.ok) {
          const remote = normalizeListResponse(data);
          if (remote.length > 0) {
            const local = readJsonStorage();
            const seen = new Set(remote.map((r) => r.url));
            const merged = [...remote, ...local.filter((l) => !seen.has(l.url))];
            writeJsonStorage(merged);
            return merged;
          }
        }
      } catch {
        // use local
      }
    }
  }
  return readJsonStorage();
}

export async function uploadMediaFile(file) {
  if (!(file instanceof File)) {
    throw new MediaApiError("Invalid file", { status: 0 });
  }
  const base = getApiBase();
  const token = getAdminJwt();
  if (!base) {
    throw new MediaApiError("API base is not configured", { status: 0 });
  }
  if (!token) {
    throw new MediaApiError("Sign in required", { status: 401 });
  }

  const form = new FormData();
  form.append("file", file);

  const uploadUrl = mediaApiUrl("upload");
  const res = await adminAuthedFetch(uploadUrl, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  const body = await res.text().then((t) => {
    if (!t) return {};
    try {
      return JSON.parse(t);
    } catch {
      return { message: t };
    }
  });

  if (!res.ok) {
    const msg =
      typeof body.message === "string"
        ? body.message
        : typeof body.error === "string"
          ? body.error
          : `Upload failed (${res.status})`;
    throw new MediaApiError(msg, { status: res.status, body });
  }

  const url = extractUrlFromUploadBody(body);
  if (!url) {
    throw new MediaApiError("Server did not return an image URL", { status: res.status, body });
  }

  return addStoredMediaItem({ url, name: file.name || "" });
}
